'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@loamify/ui';
import { canAccessAdminTools, useAuth } from '@/lib/auth-store';

const PUBLIC_PATHS = new Set(['/login']);

export function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const user = useAuth((s) => s.user);
  const hydrated = useAuth((s) => s.hydrated);
  const sessionStatus = useAuth((s) => s.sessionStatus);
  const retryHydration = useAuth((s) => s.retryHydration);

  useEffect(() => {
    if (!hydrated || sessionStatus === 'degraded' || PUBLIC_PATHS.has(pathname)) {
      return;
    }
    if (!user || !canAccessAdminTools(user)) {
      router.replace('/login');
    }
  }, [hydrated, pathname, router, sessionStatus, user]);

  if (PUBLIC_PATHS.has(pathname)) {
    return <>{children}</>;
  }

  if (!hydrated && !user) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-body-sm text-muted-foreground">Checking access…</p>
      </div>
    );
  }

  if (sessionStatus === 'degraded' && !user) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-body-sm font-medium">Admin session verification is temporarily unavailable.</p>
        <p className="max-w-md text-body-sm text-muted-foreground">
          Your secure cookie is unchanged. No private admin data will load until verification succeeds.
        </p>
        <Button type="button" variant="outline" onClick={() => void retryHydration()}>
          Retry session check
        </Button>
      </div>
    );
  }

  if (!user || !canAccessAdminTools(user)) {
    return null;
  }

  return <>{children}</>;
}
